import React, { useCallback } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  useDisclosure,
  Select,
} from "@chakra-ui/react";
import { Plus, AlertTriangle } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";
import { StoreState } from "enum/StoreState";
import { InsightPreferencesBlockProps } from "../types";
import { CreateDashboardModal } from "./CreateDashboardModal";

/**
 * InsightPreferencesBlock - Block 1 content for Event preferences
 * Contains:
 * - Dashboard selector (Select dropdown)
 * - "Criar" button to create new dashboard
 *
 * FR-013: Dashboard selector
 * FR-014: Create dashboard button with inline modal
 */
export const InsightPreferencesBlock: React.FC<InsightPreferencesBlockProps> =
  observer(
    ({
      values,
      errors,
      touched,
      setFieldValue,
      t,
      isEditMode = false,
      isDashboardLocked = false,
    }) => {
      const {
        dataStores: { operationRuleStore },
      } = useStores();

      const {
        isOpen: isCreateDashboardOpen,
        onOpen: onCreateDashboardOpen,
        onClose: onCreateDashboardClose,
      } = useDisclosure();

      // Get dashboards from store (same as Alert creation)
      const dashboards = operationRuleStore.operationRules;
      const isDashboardsLoading =
        operationRuleStore.state === StoreState.PEDDING;

      // Handle dashboard selection (same pattern as Alert creation)
      // Also activates block1Active since selecting a dashboard means user wants to create an Insight
      const handleDashboardChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
          const dashboardId = e.target.value
            ? Number(e.target.value)
            : undefined;
          setFieldValue("dashboardId", dashboardId);
          // Auto-activate block1Active when a dashboard is selected
          if (dashboardId) {
            setFieldValue("block1Active", true);
          }
        },
        [setFieldValue],
      );

      // Handle dashboard created from modal
      // Also activates block1Active since creating a dashboard means user wants to create an Insight
      const handleDashboardCreated = useCallback(
        (dashboardId: number) => {
          setFieldValue("dashboardId", dashboardId);
          setFieldValue("block1Active", true);
          onCreateDashboardClose();
        },
        [setFieldValue, onCreateDashboardClose],
      );

      // Dashboard is always required for Event creation
      const hasDashboardError = touched.dashboardId && errors.dashboardId;
      const hasDashboards = dashboards.length > 0;

      return (
        <Box>
          {/* Dashboard Selector Row */}
          <Flex gap="32px" align="flex-start">
            {/* Dashboard Selector - Same style as Alert creation */}
            <Box flex={1}>
              <FormControl isRequired isInvalid={!!hasDashboardError}>
                <FormLabel
                  fontSize="14px"
                  fontWeight="500"
                  color="#414651"
                  lineHeight="20px"
                  mb="6px"
                >
                  {isEditMode ? t("selectedDashboard") : t("selectDashboard")}
                </FormLabel>
                {hasDashboards && !isEditMode && (
                  <>
                    <Select
                      name="dashboardId"
                      placeholder={t("selectPlaceholder")}
                      value={values.dashboardId || ""}
                      onChange={handleDashboardChange}
                      isDisabled={isDashboardsLoading || isDashboardLocked}
                      borderColor="#D5D7DA"
                      _hover={{ borderColor: "#A1A1AA" }}
                      _focus={{
                        borderColor: "#0884CC",
                        boxShadow: "0 0 0 1px #0884CC",
                      }}
                      h="44px"
                      fontSize="16px"
                      color={values.dashboardId ? "#414651" : "#717680"}
                    >
                      {dashboards.map((dashboard) => (
                        <option key={dashboard.id} value={dashboard.id}>
                          {dashboard.name}
                        </option>
                      ))}
                    </Select>
                    {hasDashboardError && (
                      <FormErrorMessage>
                        {t("dashboardRequired")}
                      </FormErrorMessage>
                    )}
                  </>
                )}
                {isEditMode && (
                  <Select
                    name="dashboardId"
                    placeholder={t("selectPlaceholder")}
                    value={values.dashboardId || ""}
                    onChange={handleDashboardChange}
                    isDisabled={true}
                    borderColor="#D5D7DA"
                    h="44px"
                    fontSize="16px"
                    color="#414651"
                  >
                    {dashboards.map((dashboard) => (
                      <option key={dashboard.id} value={dashboard.id}>
                        {dashboard.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>

              {/* Help text and warning message */}
              <Text
                fontSize="12px"
                fontWeight="400"
                color="#4A6075"
                lineHeight="16px"
                mt="8px"
              >
                {t("dashboardHelpText")}
              </Text>

              {/* Empty state warning - show when no dashboards exist */}
              {!hasDashboards && !isEditMode && (
                <Flex align="center" gap="6px" mt="4px">
                  <AlertTriangle size={16} color="#0884CC" />
                  <Text
                    fontSize="12px"
                    fontWeight="400"
                    color="#0884CC"
                    lineHeight="16px"
                  >
                    {t("noDashboardWarning")}
                  </Text>
                </Flex>
              )}
            </Box>

            {/* Create Dashboard Button - Hidden in edit mode or when dashboard is locked */}
            {!isEditMode && !isDashboardLocked && (
              <Box
                as="button"
                type="button"
                onClick={onCreateDashboardOpen}
                bg="#DBF1FE"
                h="44px"
                w="145px"
                borderRadius="8px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="10px"
                cursor="pointer"
                flexShrink={0}
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
                mt="26px"
              >
                <Plus size={24} color="#0884CC" />
                <Text
                  fontSize="14px"
                  fontWeight="500"
                  color="#0884CC"
                  lineHeight="normal"
                >
                  {t("create")}
                </Text>
              </Box>
            )}
          </Flex>

          {/* Create Dashboard Modal */}
          <CreateDashboardModal
            isOpen={isCreateDashboardOpen}
            onClose={onCreateDashboardClose}
            onDashboardCreated={handleDashboardCreated}
            t={t}
          />
        </Box>
      );
    },
  );

InsightPreferencesBlock.displayName = "InsightPreferencesBlock";

export default InsightPreferencesBlock;
